import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { fieldLabelCls } from "./fieldPrimitives";

/** Role choice: a regular Player (default) or a bench Substitute. Tracked as
 *  `substituteOnly` so the existing save payloads stay unchanged. Shared by the
 *  add and edit roster dialogs so the two cannot drift apart. */
export function RoleSelector({
  substituteOnly,
  onChange,
  lockedReason,
}: {
  substituteOnly: boolean;
  onChange: (v: boolean) => void;
  /**
   * Why Player is unavailable. A finalized team league grows by a whole team, so
   * this dialog can only add a substitute there — offering Player would hand the
   * operator a form the server is certain to refuse.
   */
  lockedReason?: string;
}) {
  const options = [
    {
      sub: false,
      label: "Player",
      hint: lockedReason ?? "In the regular rotation",
      disabled: !!lockedReason,
    },
    {
      sub: true,
      label: "Substitute",
      hint: "A bench player, not in the regular rotation",
      disabled: false,
    },
  ];
  return (
    <div className="flex flex-col gap-2">
      <span className={fieldLabelCls}>Role</span>
      <div className="grid grid-cols-2 gap-3">
        {options.map((opt) => {
          const active = substituteOnly === opt.sub;
          return (
            <button
              key={opt.label}
              type="button"
              aria-pressed={active}
              disabled={opt.disabled}
              title={opt.disabled ? lockedReason : undefined}
              onClick={() => onChange(opt.sub)}
              className={cn(
                "flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-colors",
                opt.disabled && "cursor-not-allowed opacity-60",
                // A 4% primary wash was invisible on a dark surface. Selection
                // now carries on the border, the fill, and a check, so it never
                // rests on one low-contrast cue. The dark fill is stepped up
                // because the same alpha over a dark ground separates far less.
                active
                  ? "border-primary bg-primary/10 dark:border-ring dark:bg-primary/25"
                  : "border-border bg-surface hover:border-primary/30 hover:bg-muted/40",
              )}
            >
              <span className="flex w-full items-center gap-1.5">
                {/* The label stays full-contrast in both themes: Mineral Green
                    as text is only 2.4:1 on the dark selected fill. The accent
                    rides the check and the border instead, and dark mode uses
                    --ring, the lighter green that exists for exactly this. */}
                <span
                  className={cn(
                    "poppins text-sm font-semibold tracking-tight",
                    active
                      ? "text-primary dark:text-ring"
                      : "text-text-primary",
                  )}
                >
                  {opt.label}
                </span>
                {active && (
                  <Check
                    className="size-3.5 shrink-0 text-primary dark:text-ring motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-150"
                    aria-hidden
                  />
                )}
              </span>
              <span className="text-xs leading-snug text-text-muted">
                {opt.hint}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
